/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Grid, Box, Backdrop, CircularProgress, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import i18n from '../../../src/website/pages/i18n';
import ResetError from '../../pages/ResetError';
import SnackBar from '../../components/snackbar/snackbar';
import Logo from '../../assets/logo/logo.png';
import useLoginFlow from './hooks/useLoginFlow';
import LoginForm from './components/LoginForm';
import ImagePanel from './components/ImagePanel';
import { ForgotPasswordForm, ResetLinkSent, EmailVerification, ResetPasswordForm } from './components/ForgotPasswordForm';
import { ReactivateModal, LoadingOverlay, PasswordChangedModal } from './components/ReactivateModal';
import OnBoard from './loginonboard';

export default function LoginPage({ resetPasswordMode }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const flow = useLoginFlow(resetPasswordMode);

  const logoSrc = flow.branding?.logoUrl || Logo;
  const [loginFormHeight, setLoginFormHeight] = useState('');


   useEffect(() => {
    if (!flow.showLearningJourney) return undefined;
    window.history.pushState(null, '', '/dashboard');
    const blockBack = () => window.history.pushState(null, '', '/dashboard');
    window.addEventListener('popstate', blockBack);
    return () => window.removeEventListener('popstate', blockBack);
  }, [flow.showLearningJourney]);

  const updateHeight = () => {
    const loginForm = document.getElementById('loginFormIs');
    if (loginForm) setLoginFormHeight(loginForm.offsetHeight - 28);
  };

  useEffect(() => {
    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('load', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('load', updateHeight);
    };
  }, []);

  const activeScreen = (() => {
    if (flow.showEmailVerification) return 'emailVerify';
    if (resetPasswordMode === 'sent') return 'resetSent';
    if (flow.emailFrom) return 'verify';
    if (flow.sendEmail) return 'sent';
    if (flow.forgetClick) return 'forgot';
    return 'login';
  })();

  const [onboardingLoading, setOnboardingLoading] = useState(false);

  const handleOnBoardComplete = async () => {
    setOnboardingLoading(true);
    flow.setShowLearningJourney(false);
    await flow.getLoginUserDetails();
    setOnboardingLoading(false);
  };

  if (flow.verifyLoader && resetPasswordMode === 'verify') {
    return (
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
        }}
        open
      >
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <CircularProgress color="primary" size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.primary' }}>
            Please wait while we update...
          </Typography>
        </Box>
      </Backdrop>
    );
  }

  return (
    <Box className={classes.mainContainer}>
      <Container
        maxWidth="lg"
        style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Grid
          container
          spacing={2}
          id="loginPanel"
          alignItems="stretch"
          sx={{ justifyContent: 'center !important' }}
        >
          <>
            {(activeScreen === 'login' && flow.showLearningJourney === false) && (
              <Grid item xs={12} md={4.5} id="userIdlogin" sx={{ margin: 'auto' }}>
                <Box id="loginFormIs" className={classes.formcontainer} style={{ height: flow.statusAlert ? '720px':'660px' }}>
                  <LoginForm
                    savedEmail={flow.savedEmail}
                    savedPassword={flow.savedPassword}
                    loading={flow.loading}
                    disableEmail={flow.disableEmail}
                    statusAlert={flow.statusAlert}
                    emailError={flow.emailError}                        
                    onEmailFieldChange={() => flow.setEmailError('')}
                    logoSrc={logoSrc}
                    onSubmit={flow.handleSubmitForm}
                    onForgotPassword={flow.forgetPassword}
                    onSignUp={() => navigate('/sign-up')}
                    onDismissAlert={() => flow.setStatusAlert(null)}
                    onGoogleSuccess={flow.handleGoogleLogin}
                    onGoogleError={flow.handleGoogleLoginError}
                  />
                </Box>
              </Grid>
            )}

            {activeScreen === 'forgot' && (
              <Grid item xs={12} md={4.5} sx={{ margin: 'auto' }}>
                <Box id="loginFormIs" className={classes.formcontainer}>
                  <ForgotPasswordForm
                    savedEmail={flow.savedEmail}
                    forgetloading={flow.forgetloading}
                    showEmailNotRegistered={flow.showEmailNotRegistered}
                    logoSrc={logoSrc}
                    onSubmit={flow.handleResetPasswordClick}
                    onBack={flow.backToLogin}
                    statusAlert={flow.statusAlert}
                    onDismissAlert={() => flow.setStatusAlert(null)}
                  />
                </Box>
              </Grid>
            )}

            {activeScreen === 'sent' && (
              <Grid item xs={12} md={4.5} sx={{ margin: 'auto' }}>
                <Box id="loginFormIs" className={classes.formcontainer}>
                  <ResetLinkSent
                    forgetEmail={flow.forgetEmail}
                    loading={flow.loading}
                    logoSrc={logoSrc}
                    onTryAgain={() => {
                      flow.setSendEmail(false);
                      navigate('/reset-password', { replace: true });
                    }}
                    onBack={flow.backToLogin}
                  />
                </Box>
              </Grid>
            )}

            {activeScreen === 'emailVerify' && (
              <Grid item xs={12} md={4.5} id="emailVerificationSection" sx={{ margin: 'auto', paddingRight: '10px' }}>
                <Box className={classes.formcontainer} style={{ display: 'flex', alignItems: 'center' }}>
                  <EmailVerification
                    resendEmailLoading={flow.resendEmailLoading}
                    logoSrc={logoSrc}
                    onResend={flow.handleResendEmail}
                    onBack={flow.backToLogin}
                  />
                </Box>
              </Grid>
            )}

            {activeScreen === 'verify' && (
              flow.isValid && flow.verificationData ? (
                <Grid item xs={12} md={4.5}>
                  <Box
                    className={classes.formcontainer}
                    id="resetPasswordPanel"
                    sx={{
                      borderRight: '10px solid #f0f0f0',
                      height: '630px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <ResetPasswordForm
                      passwordValue={flow.passwordValue}
                      confirmPasswordValue={flow.confirmPasswordValue}
                      setPasswordValue={flow.setPasswordValue}
                      setConfirmPasswordValue={flow.setConfirmPasswordValue}
                      passwordError={flow.passwordError}
                      setPasswordError={flow.setPasswordError}
                      showPasswordLengthError={flow.showPasswordLengthError}
                      setShowPasswordLengthError={flow.setShowPasswordLengthError}
                      showPasswordMismatchError={flow.showPasswordMismatchError}
                      setShowPasswordMismatchError={flow.setShowPasswordMismatchError}
                      changePasswordLoading={flow.changePasswordLoading}
                      logoSrc={logoSrc}
                      onSubmit={flow.handleChangePassword}
                      onBack={flow.backToLogin}
                    />
                  </Box>
                </Grid>
              ) : (
                !flow.verifyLoader && (
                  <ResetError errorMessage={flow.tokenError} onBackToLogin={flow.backToLogin} />
                )
              )
            )}

            {activeScreen === 'resetSent' && (
              <Grid item xs={12} md={4.5} >
                <Box id="loginFormIs" className={classes.formcontainer} sx={{
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  <ResetLinkSent
                    forgetEmail={flow.forgetEmail}
                    loading={flow.loading}
                    logoSrc={logoSrc}
                    onTryAgain={() => {
                      flow.setSendEmail(false);
                      navigate('/reset-password', { replace: true });
                    }}
                    onBack={flow.backToLogin}
                  />
                </Box>
              </Grid>
            )}
          </>

          {(flow.isValid && flow.showLearningJourney === false) && (  
            <Grid id="hideSlider" item xs={12} md={7.5}  sx={{height: flow.statusAlert && '720px' }}>
              <ImagePanel screen={activeScreen} />
            </Grid>
          )}
        </Grid>
      </Container>

      <ReactivateModal
        open={flow.showReactivateModal}
        reactivateState={flow.reactivateState}
        setReactivateState={flow.setReactivateState}
        reactivateLoading={flow.reactivateLoading}
        onReactivate={flow.handleReactivate}
        onKeepWaiting={flow.handleKeepWaiting}
      />

      <PasswordChangedModal
        open={flow.isModalOpen}
        changePasswordLoading={flow.changePasswordLoading}
        onClose={flow.closeSuccessModal}
      />

      {flow.showLearningJourney && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            overflowY: 'auto',
            zIndex: 1300,
            backgroundColor: 'rgba(255,255,255,0.95)',
          }}
        >
          <OnBoard onComplete={handleOnBoardComplete} comingfrom="login"  comingJourney={flow.comingJourney}/>
        </Box>
      )}

      {onboardingLoading && (
        <Backdrop
          open
          sx={{
            zIndex: 1400,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress color="primary" size={56} thickness={4} />
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
            Setting up your workspace...
          </Typography>
        </Backdrop>
      )}

      <LoadingOverlay open={flow.loading && activeScreen === 'login'} />

      <SnackBar
        open={flow.openSnackbarState}
        snackbarTitle={flow.snackbarTitle}
        close={() => flow.setOpenSnackbarState(false)}
        comingfrom="login"
      />
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: '#F0F0F0',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
  },
  formcontainer: {
    background: '#FFFFFF',
    padding: '36px',
    borderRadius: '20px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  '@global': {
    '#hideSlider .carousel, \
   #hideSlider .carousel .slider-wrapper, \
   #hideSlider .carousel .slider, \
   #hideSlider .carousel .slide, \
   #hideSlider .carousel .slide > div': {
      height: '100%',
    },

    '#hideSlider .carousel .slide > div': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    '#hideSlider .carousel .slide img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
      margin: 'auto',
    },
  },
}));